import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { portalApi, usePortal } from "@/lib/PortalContext";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Heart, Check, MessageCircleQuestion, LogOut, MessageSquare } from "lucide-react";
import AuthedImage from "@/components/AuthedImage";
import SchematicPreview from "@/components/SchematicPreview";

const CURRENCY = (n) => (n == null ? null : `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);

export default function PortalConceptsPage() {
  const { id, dvid } = useParams();
  const nav = useNavigate();
  const { client, logout } = usePortal();
  const [data, setData] = useState(null);           // list mode payload
  const [concept, setConcept] = useState(null);     // single concept payload
  const [busyAction, setBusyAction] = useState(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (client === null) nav("/portal/login");
  }, [client, nav]);

  const load = useCallback(async () => {
    if (!client) return;
    try {
      if (dvid) {
        const r = await portalApi.get(`/portal/projects/${id}/concepts/${dvid}`);
        setConcept(r.data);
      } else {
        const r = await portalApi.get(`/portal/projects/${id}/concepts`);
        setData(r.data);
      }
    } catch (e) {
      toast.error(formatErr(e) || "Could not load concepts");
      nav(`/portal/projects/${id}`);
    }
  }, [client, id, dvid, nav]);

  useEffect(() => { load(); }, [load]);

  const sendFeedback = async (action) => {
    if (!concept) return;
    if (action === "comment" && !comment.trim()) { toast.error("Please write a comment first"); return; }
    setBusyAction(action);
    try {
      await portalApi.post(`/portal/design-versions/${concept.id}/feedback`, {
        action, comment: action === "comment" ? comment.trim() : "",
      });
      const label = { love: "Love noted", approve: "Direction approved", request: "Change request sent", comment: "Comment sent" }[action];
      toast.success(`${label} — the studio will follow up shortly.`);
      if (action === "comment") setComment("");
      await load();
    } catch (e) {
      toast.error(formatErr(e) || "Could not save feedback");
    } finally {
      setBusyAction(null);
    }
  };

  if (!client) return null;

  return (
    <div className="min-h-screen bg-[color:hsl(var(--background))]" data-testid="portal-concepts">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-6 flex justify-between items-center">
          <Link to={`/portal/projects/${id}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="link-back-project">
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Back to project
          </Link>
          <button onClick={async () => { await logout(); nav("/"); }} className="text-xs text-muted-foreground inline-flex gap-1"><LogOut className="w-3 h-3" strokeWidth={1.5}/> Sign out</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        {dvid && concept ? (
          <ConceptDetail
            concept={concept}
            sendFeedback={sendFeedback}
            busyAction={busyAction}
            comment={comment}
            setComment={setComment}
          />
        ) : data ? (
          <ConceptsList data={data} projectId={id}/>
        ) : (
          <div className="text-sm text-muted-foreground">Loading…</div>
        )}
      </main>
    </div>
  );
}

function ConceptsList({ data, projectId }) {
  const concepts = data.concepts || [];
  if (concepts.length === 0) {
    return (
      <div>
        <div className="overline">Design Concepts</div>
        <h1 className="font-serif font-light mt-3" style={{ fontSize: "clamp(38px, 5vw, 52px)", lineHeight: 1.05 }}>Nothing to show yet</h1>
        <p className="text-muted-foreground mt-4 max-w-xl">Your studio hasn't released a concept for this project yet. You'll be notified by email as soon as one is ready to review.</p>
      </div>
    );
  }
  const current = concepts.find((c) => c.status !== "superseded") || concepts[0];
  const history = concepts.filter((c) => c.id !== current.id);
  return (
    <div>
      <div className="overline">{data.project?.project_number}</div>
      <h1 className="font-serif font-light mt-3" style={{ fontSize: "clamp(38px, 5vw, 52px)", lineHeight: 1.05 }}>{data.project?.title} — Design Concepts</h1>
      <p className="text-muted-foreground mt-4 max-w-2xl">Every concept your studio has released for you, with the latest one shown first.</p>

      <div className="mt-10">
        <ConceptCard concept={current} projectId={projectId} isCurrent testid={`concept-card-${current.id}`}/>
      </div>

      {history.length > 0 && (
        <div className="mt-14">
          <div className="overline">Previous Concepts</div>
          <div className="mt-5 space-y-4">
            {history.map((c) => <ConceptCard key={c.id} concept={c} projectId={projectId} testid={`concept-card-${c.id}`}/>)}
          </div>
        </div>
      )}
    </div>
  );
}

function ConceptCard({ concept, projectId, isCurrent, testid }) {
  return (
    <Link to={`/portal/projects/${projectId}/concepts/${concept.id}`} className="luxe-card p-6 flex flex-col md:flex-row gap-6 hover:border-foreground transition-colors block" data-testid={testid}>
      <div className="md:w-52 shrink-0">
        <SchematicPreview schematic={concept.schematic_snapshot} height={130}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline text-[10px]">Version {concept.version} · {statusLabel(concept.status)}</div>
            <div className="font-serif text-2xl mt-1">{concept.title}</div>
          </div>
          {isCurrent && (
            <span className="text-[10px] px-2 py-1" style={{ background: "#A68A64", color: "white", letterSpacing: "0.08em" }} data-testid="badge-current">CURRENT</span>
          )}
        </div>
        {concept.client_description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{concept.client_description}</p>}
        <div className="text-xs text-muted-foreground mt-3">
          Released {concept.released_at ? new Date(concept.released_at).toLocaleDateString("en-AU") : "—"}
          {concept.indicative_total && <> · Indicative investment {CURRENCY(concept.indicative_total)} AUD</>}
        </div>
      </div>
    </Link>
  );
}

function ConceptDetail({ concept, sendFeedback, busyAction, comment, setComment }) {
  const alreadyLoved = (concept.my_feedback || []).some((f) => f.action === "love");
  const alreadyApproved = (concept.my_feedback || []).some((f) => f.action === "approve");
  return (
    <div data-testid="concept-detail">
      <div className="overline">Version {concept.version} · {statusLabel(concept.status)}</div>
      <h1 className="font-serif font-light mt-3" style={{ fontSize: "clamp(38px, 5vw, 52px)", lineHeight: 1.05 }} data-testid="concept-title">{concept.title}</h1>
      <div className="text-xs text-muted-foreground mt-3">
        Released {concept.released_at ? new Date(concept.released_at).toLocaleDateString("en-AU") : "—"}
        {concept.indicative_total && <> · Indicative investment {CURRENCY(concept.indicative_total)} AUD (subject to final proposal)</>}
      </div>

      {/* Schematic */}
      <div className="mt-10 luxe-card p-6">
        <div className="overline mb-3">Elevation</div>
        <SchematicPreview schematic={concept.schematic_snapshot} height={280}/>
      </div>

      {/* Description */}
      {concept.client_description && (
        <div className="mt-10">
          <div className="overline">The Idea</div>
          <p className="mt-3 text-lg leading-relaxed max-w-3xl" data-testid="concept-description">{concept.client_description}</p>
        </div>
      )}

      {/* Key features */}
      {concept.key_features?.length > 0 && (
        <div className="mt-10">
          <div className="overline">Key Features</div>
          <ul className="mt-3 grid md:grid-cols-2 gap-2 max-w-3xl" data-testid="concept-features">
            {concept.key_features.map((f, i) => (
              <li key={i} className="flex gap-2 text-sm items-start">
                <span className="w-1 h-1 rounded-full bg-foreground mt-2 shrink-0"/>{f}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Materials */}
      <div className="mt-10">
        <div className="overline">Material Palette</div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {(concept.line_items_snapshot || []).map((m) => (
            <div key={m.id} className="luxe-card p-4 flex gap-3" data-testid={`concept-material-${m.id}`}>
              <div className="w-16 h-16 bg-muted overflow-hidden shrink-0">
                <AuthedImage path={m.image_path} className="w-full h-full object-cover"/>
              </div>
              <div className="min-w-0">
                <div className="overline text-[10px]">{m.category}</div>
                <div className="font-serif text-base leading-tight">{m.name}</div>
                {m.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.description}</div>}
              </div>
            </div>
          ))}
          {(!concept.line_items_snapshot || concept.line_items_snapshot.length === 0) && (
            <div className="text-sm text-muted-foreground">Material palette details will be sent alongside the formal proposal.</div>
          )}
        </div>
      </div>

      {/* Feedback */}
      <div className="mt-14">
        <div className="overline">Your Feedback</div>
        <h2 className="font-serif font-light mt-2" style={{ fontSize: "30px" }}>How does this feel?</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Your reaction stays attached to this exact concept version. You can react on future versions independently.</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-6 max-w-2xl">
          <FeedbackButton
            testid="btn-fb-love"
            icon={Heart} label="Love This" tone="accent"
            busy={busyAction === "love"} disabled={alreadyLoved}
            onClick={() => sendFeedback("love")}
            active={alreadyLoved}
          />
          <FeedbackButton
            testid="btn-fb-approve"
            icon={Check} label="Approve Direction" tone="dark"
            busy={busyAction === "approve"} disabled={alreadyApproved}
            onClick={() => sendFeedback("approve")}
            active={alreadyApproved}
          />
          <FeedbackButton
            testid="btn-fb-request"
            icon={MessageCircleQuestion} label="Request Changes"
            busy={busyAction === "request"}
            onClick={() => sendFeedback("request")}
          />
        </div>
        <div className="mt-6 max-w-2xl">
          <label className="overline text-[10px] block mb-2">Add a comment</label>
          <textarea
            data-testid="input-fb-comment"
            className="luxe-input"
            rows={3}
            placeholder="e.g. Love the stone, but could we see a lighter oak?"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="mt-3 flex justify-end">
            <button
              data-testid="btn-fb-comment"
              onClick={() => sendFeedback("comment")}
              disabled={busyAction === "comment" || !comment.trim()}
              className="btn-primary"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.5}/>
              {busyAction === "comment" ? "Sending…" : "Send comment"}
            </button>
          </div>
        </div>

        {(concept.my_feedback || []).length > 0 && (
          <div className="mt-10">
            <div className="overline">Your previous reactions on this version</div>
            <div className="mt-4 space-y-2" data-testid="my-feedback-list">
              {concept.my_feedback.map((f) => (
                <div key={f.id} className="text-sm border-l-2 border-[color:hsl(var(--accent))] pl-4 py-1">
                  <div className="text-xs text-muted-foreground">{new Date(f.created_at).toLocaleString("en-AU")} · {actionLabel(f.action)}</div>
                  {f.comment && <div className="mt-1">{f.comment}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FeedbackButton({ icon: Icon, label, testid, onClick, busy, disabled, tone, active }) {
  const base = "px-4 py-3 border text-sm flex items-center gap-2 justify-center transition-colors disabled:opacity-60";
  const style = active
    ? "bg-foreground text-background border-foreground"
    : tone === "accent"
      ? "border-[color:hsl(var(--accent))] hover:bg-[color:hsl(var(--accent))] hover:text-white"
      : "border-input hover:border-foreground";
  return (
    <button data-testid={testid} onClick={onClick} disabled={busy || disabled} className={`${base} ${style}`}>
      <Icon className="w-4 h-4" strokeWidth={1.5}/>
      {busy ? "Sending…" : (active ? `${label} ✓` : label)}
    </button>
  );
}

function statusLabel(s) {
  return ({
    draft: "Draft",
    released: "Current concept",
    feedback_received: "Feedback received",
    approved_direction: "Direction approved",
    superseded: "Previous version",
  })[s] || s;
}
function actionLabel(a) {
  return ({ love: "Loved", approve: "Approved direction", request: "Requested changes", comment: "Commented" })[a] || a;
}
